import React from 'react';
import btcIcon from 'cryptocurrency-icons/svg/color/btc.svg';
import ethIcon from 'cryptocurrency-icons/svg/color/eth.svg';
import usdcIcon from 'cryptocurrency-icons/svg/color/usdc.svg';
import usdtIcon from 'cryptocurrency-icons/svg/color/usdt.svg';
import solIcon from 'cryptocurrency-icons/svg/color/sol.svg';
import bnbIcon from 'cryptocurrency-icons/svg/color/bnb.svg';
import xrpIcon from 'cryptocurrency-icons/svg/color/xrp.svg';
import maticIcon from 'cryptocurrency-icons/svg/color/matic.svg';
import paxgIcon from 'cryptocurrency-icons/svg/color/paxg.svg';
import genericAssetIcon from 'cryptocurrency-icons/svg/color/generic.svg';

export const normalizeAssetKey = (assetKey) => String(assetKey || '').toLowerCase();

const PACKAGE_ICON_BY_ASSET = {
  btc: btcIcon,
  eth: ethIcon,
  usdc: usdcIcon,
  usdt: usdtIcon,
  sol: solIcon,
  bnb: bnbIcon,
  xrp: xrpIcon,
  matic: maticIcon,
  paxg: paxgIcon,
};

const buildKnownImageIcon = ({ normalized, size, className }) => (
  <img
    src={PACKAGE_ICON_BY_ASSET[normalized]}
    alt=""
    width={size}
    height={size}
    className={`rounded-full object-contain ${className}`.trim()}
    aria-hidden="true"
    data-testid={`options-asset-icon-${normalized}`}
  />
);

const SELECTOR_ICON_CONFIG = {
  btc: { background: '#F7931A', symbol: '\u20BF' },
  usd: { background: '#2775CA', symbol: '$' },
};

const buildNeutralSelectorSymbol = ({ assetKey, size, className }) => {
  const config = SELECTOR_ICON_CONFIG[assetKey];
  if (!config) return null;

  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true" data-testid={`options-asset-icon-${assetKey}`}>
      <circle cx="12" cy="12" r="12" fill={config.background} />
      <text
        x="12"
        y="16.3"
        textAnchor="middle"
        fontSize="13"
        fontWeight="700"
        fill="#FFFFFF"
        fontFamily="Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
      >
        {config.symbol}
      </text>
    </svg>
  );
};

export const OptionsAssetIcon = ({ assetKey, size = 18, variant = 'default', className = '' }) => {
  const normalized = normalizeAssetKey(assetKey);

  if (variant === 'symbol') {
    const symbolIcon = buildNeutralSelectorSymbol({ assetKey: normalized, size, className });
    if (symbolIcon) return symbolIcon;
  }

  if (PACKAGE_ICON_BY_ASSET[normalized]) {
    return buildKnownImageIcon({ normalized, size, className });
  }

  if (normalized === 'usd') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true" data-testid={`options-asset-icon-${normalized}`}>
        <circle cx="12" cy="12" r="12" fill="#1F2937" />
        <circle cx="12" cy="12" r="8.2" stroke="#C8D1DE" strokeWidth="1.2" opacity="0.7" />
        <path d="M12 6.65v10.7M14.66 8.7c0-1.02-.98-1.67-2.44-1.67-1.44 0-2.42.7-2.42 1.67 0 .92.82 1.39 2.6 1.8 1.86.42 2.88.92 2.88 2.15 0 1.11-1.03 1.93-2.7 1.93-1.72 0-2.86-.79-2.86-1.93" stroke="white" strokeWidth="1.55" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (normalized === 'steth') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true" data-testid={`options-asset-icon-${normalized}`}>
        <circle cx="12" cy="12" r="12" fill="#00A3FF" />
        <path d="M12 3.8 7.9 11l4.1 2.2 4.1-2.2L12 3.8Z" fill="white" />
        <path d="M12 13.7 7.9 11.4 12 20.2l4.1-8.8-4.1 2.3Z" fill="#D7F4FF" opacity="0.92" />
        <path d="M12 13.1 7.9 10.8 12 8.95l4.1 1.85L12 13.1Z" fill="#B0E7FF" opacity="0.75" />
      </svg>
    );
  }

  if (normalized === 'eurr') {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true" data-testid={`options-asset-icon-${normalized}`}>
        <circle cx="12" cy="12" r="12" fill="#1D4ED8" />
        <path d="M15.9 8.05H9.9c-1.63 0-2.6.8-2.6 2.15 0 1.32.93 2.12 2.52 2.12h5.15" stroke="white" strokeWidth="1.55" strokeLinecap="round" />
        <path d="M15.2 6.35 8.8 6.35" stroke="#DBEAFE" strokeWidth="1.25" strokeLinecap="round" opacity="0.95" />
        <path d="M15.2 17.65 8.8 17.65" stroke="#DBEAFE" strokeWidth="1.25" strokeLinecap="round" opacity="0.95" />
        <path d="M16.2 10.2c.25 1.2-.25 2.8-1.6 3.6-1.55.9-3.72.76-5.2-.22" stroke="white" strokeWidth="1.55" strokeLinecap="round" />
      </svg>
    );
  }

  if (normalized === 'usde' || normalized === 'usyc' || normalized === 'buidl') {
    const palette = normalized === 'usde'
      ? { fill: '#111827', ring: '#22C55E', text: 'DE' }
      : normalized === 'usyc'
        ? { fill: '#0F172A', ring: '#38BDF8', text: 'YC' }
        : { fill: '#1E293B', ring: '#F8FAFC', text: 'BD' };
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true" data-testid={`options-asset-icon-${normalized}`}>
        <circle cx="12" cy="12" r="12" fill={palette.fill} />
        <circle cx="12" cy="12" r="7.35" stroke={palette.ring} strokeWidth="1.4" opacity="0.95" />
        <text x="12" y="14.35" textAnchor="middle" fontSize="6.2" fontWeight="700" fill="white">{palette.text}</text>
      </svg>
    );
  }

  return (
    <span className={`inline-flex items-center justify-center overflow-hidden rounded-full ${className}`.trim()} data-testid={`options-asset-icon-${normalized || 'generic'}`}>
      <img src={genericAssetIcon} alt="" width={size} height={size} className="rounded-full object-contain" aria-hidden="true" />
    </span>
  );
};

export default OptionsAssetIcon;